import AddIcon from '@mui/icons-material/Add';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArchiveIcon from '@mui/icons-material/Archive';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LinkIcon from '@mui/icons-material/Link';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type React from 'react';
import type { Tender } from '../../../types/tender';
import { normalizeKeyword, tokensMatch } from '../../../utils/publicTenderRelevance';
import { EstimatedCostChip } from '../../common/EstimatedCostChip';
import {
  displayDaysUntilDeadline,
  formatDisplayId,
  getRelevanceStars,
  getTenderSourceString,
  isTenderClosed,
  parseSubmissionDeadlineInTable,
} from './tableUtils';

interface PublicTenderCardProps {
  tender: Tender;
  isRead: boolean;
  isPursued: boolean;
  isAdding: boolean;
  isLoading: boolean;
  pursuedInfo?: { ownerName: string; teamName: string; ownerId: string };
  relevanceScore: number;
  relevanceStars?: 0 | 1 | 2 | 3 | undefined;
  matchedKeywords?: string[] | undefined;
  onAddKeyword?: ((keyword: string) => void) | undefined;
  onMuteKeyword?: ((keyword: string) => void) | undefined;
  onOpenDetails: () => void;
  onAddTender: () => void;
  onArchive: () => void;
  // biome-ignore lint/suspicious/noExplicitAny: Pricing settings
  pricingSettings: any;
}

const PublicTenderCard: React.FC<PublicTenderCardProps> = ({
  tender,
  isRead,
  isPursued,
  isAdding,
  isLoading,
  pursuedInfo,
  relevanceScore,
  relevanceStars,
  matchedKeywords = [],
  onAddKeyword,
  onMuteKeyword,
  onOpenDetails,
  onAddTender,
  onArchive,
  pricingSettings,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const primaryButtonStyle = {
    backgroundColor: theme.palette.greyLight,
    color: `${theme.palette.text.primary} !important`,
    fontWeight: 'bold',
    fontSize: '1.0rem',
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    border: 'none',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.greyContrast,
      boxShadow: theme.shadows[4],
      color: `${theme.palette.primary.contrastText} !important`,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  };

  const secondaryButtonStyle = {
    backgroundColor: theme.palette.bidlogiq.brick,
    color: `${theme.palette.common.white} !important`,
    fontSize: '1.0rem',
    fontWeight: 'bold',
    padding: theme.spacing(0.75, 2),
    textTransform: 'none',
    border: 'none',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.bidlogiq.brick,
      boxShadow: theme.shadows[4],
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  };

  const deadlineDateObj = parseSubmissionDeadlineInTable(tender.submissionDeadline || '');
  const closingDateDisplay = deadlineDateObj
    ? deadlineDateObj.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'N/A';
  const daysUntil = displayDaysUntilDeadline(deadlineDateObj);
  const displayClosing =
    closingDateDisplay === 'N/A' ? 'Unknown, visit portal' : `${closingDateDisplay} (${daysUntil})`;

  const llmDate = tender.llmProcessingTimestamp
    ? tender.llmProcessingTimestamp instanceof Date
      ? tender.llmProcessingTimestamp
      : new Date(tender.llmProcessingTimestamp)
    : null;
  const formattedAddedDate =
    llmDate && !Number.isNaN(llmDate.getTime())
      ? llmDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : 'N/A';

  const updatedDateRaw = tender.dateUpdated;
  let updatedDate: Date | null = null;
  if (updatedDateRaw) {
    if (
      typeof updatedDateRaw === 'object' &&
      'toDate' in updatedDateRaw &&
      typeof (updatedDateRaw as { toDate: () => Date }).toDate === 'function'
    ) {
      updatedDate = (updatedDateRaw as { toDate: () => Date }).toDate();
    } else if (updatedDateRaw instanceof Date) {
      updatedDate = updatedDateRaw;
    } else {
      updatedDate = new Date(updatedDateRaw as string | number);
    }
  }

  const formattedUpdatedDate =
    updatedDate && !Number.isNaN(updatedDate.getTime())
      ? updatedDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : null;

  const title = tender.title || 'No Title Available';
  const client = tender.tenderIssuer || 'N/A';
  const rawKeywords = tender.keywords
    ? Array.isArray(tender.keywords)
      ? tender.keywords
      : String(tender.keywords)
          .split(',')
          .map((kw) => kw.trim())
          .filter((kw) => kw)
    : [];
  const keywords = Array.from(
    new Set(
      (rawKeywords as string[])
        .map((kw) => String(kw).replace(/_/g, ' ').replace(/\s+/g, ' ').trim())
        .filter(Boolean)
    )
  );
  const starNodes = getRelevanceStars(relevanceScore, relevanceStars);
  const hasHighRelevance = Boolean(starNodes);
  const isClosed = isTenderClosed(deadlineDateObj);

  // Build a single unified list of chips (matched chips highlighted first, unmatched plain chips after)
  const coveredMatchedNorms = new Set<string>();
  const tenderChipItems = keywords.slice(0, 5).map((kw) => {
    const matchedProfileKw = matchedKeywords.find((mkw) => tokensMatch(kw, mkw) >= 0.8);
    if (matchedProfileKw) {
      coveredMatchedNorms.add(normalizeKeyword(matchedProfileKw));
    }
    return {
      label: kw,
      isMatched: Boolean(matchedProfileKw),
      profileKeyword: matchedProfileKw || kw,
    };
  });

  const extraMatchedItems = matchedKeywords
    .filter((mkw) => !coveredMatchedNorms.has(normalizeKeyword(mkw)))
    .slice(0, 3)
    .map((mkw) => ({
      label: mkw,
      isMatched: true,
      profileKeyword: mkw,
    }));

  const unifiedChips = [
    ...tenderChipItems.filter((c) => c.isMatched),
    ...extraMatchedItems,
    ...tenderChipItems.filter((c) => !c.isMatched),
  ];

  return (
    <Card
      role="article"
      sx={{
        mb: 2.5,
        boxShadow: `0 2px 4px ${!isRead ? theme.palette.primary.dark : theme.palette.greyContrast}`,
        borderLeft: `6px solid ${isClosed ? '#b7410e' : !isRead ? theme.palette.bidlogiq.orange : theme.palette.greyContrast}`,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.greyLight,
        color: theme.palette.text.primary,
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ pb: '8px !important', flexGrow: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: isRead ? 'normal' : 'bold',
                color: theme.palette.text.primary,
                pr: 1,
              }}
            >
              {title}
              {isPursued && pursuedInfo && (
                <Chip
                  label={`OWNER: ${pursuedInfo.ownerName} (${pursuedInfo.teamName})`}
                  size="small"
                  sx={{
                    fontWeight: 'bold',
                    ml: 1,
                    verticalAlign: 'middle',
                    mt: -0.5,
                    backgroundColor: theme.palette.greyContrast,
                    color: theme.palette.getContrastText(theme.palette.greyContrast),
                    border: `1px solid ${theme.palette.bidlogiq.orange}`,
                  }}
                />
              )}
              {tender.noFiles && (
                <Chip
                  label="No Docs, Visit Portal"
                  component="a"
                  href={tender.tenderSourceURL || ''}
                  target="_blank"
                  clickable
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 'bold',
                    ml: 1,
                    verticalAlign: 'middle',
                    mt: -0.5,
                    backgroundColor: theme.palette.greyContrast,
                    color: theme.palette.primary.contrastText,
                    borderColor: theme.palette.bidlogiq.orange,
                    borderWidth: 2,
                    fontSize: '0.85rem',
                    '&&:hover': {
                      backgroundColor: theme.palette.bidlogiq.brick,
                      color: '#fff',
                      borderColor: theme.palette.bidlogiq.brick,
                    },
                  }}
                />
              )}
            </Typography>
          </Box>
          {hasHighRelevance && (
            <Tooltip
              title={
                matchedKeywords.length > 0
                  ? `Relevance score: ${relevanceScore.toFixed(1)} (Matched: ${matchedKeywords.slice(0, 5).join(', ')})`
                  : `Relevance score: ${relevanceScore.toFixed(1)}`
              }
              arrow
            >
              <Box
                sx={{
                  backgroundColor: 'transparent',
                  borderRadius: '16px',
                  px: 1,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px',
                  flexShrink: 0,
                }}
              >
                {starNodes}
              </Box>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{ mb: 0.4, fontSize: '1rem', color: theme.palette.text.primary }}
            >
              Issued by: {client}
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 0.4, fontSize: '1rem', color: theme.palette.text.primary }}
            >
              Date added: {formattedAddedDate}
              {formattedUpdatedDate && (
                <span style={{ fontStyle: 'italic', fontSize: '0.9rem', marginLeft: '8px' }}>
                  (updated: {formattedUpdatedDate})
                </span>
              )}
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 0.4, fontSize: '1rem', color: theme.palette.text.primary }}
            >
              Closing: {displayClosing}
            </Typography>
            {tender.tenderSourceURL && (
              <Typography
                variant="body1"
                sx={{
                  mb: 0.4,
                  fontSize: '1rem',
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  wordBreak: 'break-all',
                }}
              >
                <LinkIcon fontSize="small" sx={{ color: theme.palette.text.primary }} />
                Tender ID:{' '}
                <a
                  href={tender.tenderSourceURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: theme.palette.text.primary,
                    textDecoration: 'underline',
                    fontWeight: 600,
                  }}
                >
                  {formatDisplayId(tender.tenderId || tender.opportunityID || tender.id)}
                  {getTenderSourceString(tender.tenderId || tender.opportunityID || tender.id)
                    ? ` (${getTenderSourceString(tender.tenderId || tender.opportunityID || tender.id)})`
                    : ''}
                </a>
              </Typography>
            )}
          </Box>
        </Box>
        {unifiedChips.length > 0 && (
          <Box
            sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}
          >
            {unifiedChips.map((chipItem) =>
              chipItem.isMatched ? (
                <Chip
                  key={`chip-matched-${chipItem.label}`}
                  icon={
                    <AutoAwesomeIcon
                      sx={{ fontSize: '0.9rem !important', color: '#FDBA74 !important' }}
                    />
                  }
                  label={chipItem.label}
                  size="small"
                  onClick={onMuteKeyword ? () => onMuteKeyword(chipItem.profileKeyword) : undefined}
                  onDelete={
                    onMuteKeyword ? () => onMuteKeyword(chipItem.profileKeyword) : undefined
                  }
                  sx={{
                    backgroundColor: '#1E293B',
                    color: '#FFFFFF',
                    border: '1.5px solid #EA580C',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    '&:hover': {
                      backgroundColor: '#0F172A',
                    },
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(255, 255, 255, 0.85)',
                      '&:hover': {
                        color: '#FFFFFF',
                      },
                    },
                  }}
                />
              ) : (
                <Chip
                  key={`chip-plain-${chipItem.label}`}
                  label={chipItem.label}
                  size="small"
                  deleteIcon={<AddCircleIcon data-testid="AddKeywordIcon" />}
                  onClick={onAddKeyword ? () => onAddKeyword(chipItem.label) : undefined}
                  onDelete={onAddKeyword ? () => onAddKeyword(chipItem.label) : undefined}
                  sx={{
                    backgroundColor: theme.palette.bidlogiq.taupe,
                    color: '#111827',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(17, 24, 39, 0.65)',
                      '&:hover': {
                        color: '#111827',
                      },
                    },
                  }}
                />
              )
            )}
            {keywords.length > 5 && (
              <Chip
                label={`+${keywords.length - 5} more`}
                size="small"
                sx={{
                  backgroundColor: theme.palette.bidlogiq.taupe,
                  color: '#111827',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
        )}
      </CardContent>
      <CardActions
        sx={{
          justifyContent: 'space-between',
          p: '8px 16px',
          borderTop: `1px solid ${theme.palette.greyContrast}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="contained" size="medium" onClick={onOpenDetails} sx={primaryButtonStyle}>
            Details
          </Button>
          <Button
            variant="contained"
            size="medium"
            startIcon={
              isAdding ? (
                <CircularProgress size={20} sx={{ color: theme.palette.bidlogiq?.primaryOrange }} />
              ) : (
                <AddIcon />
              )
            }
            onClick={onAddTender}
            disabled={isPursued || isClosed || isAdding || isLoading}
            sx={[
              {
                minWidth: isSmallScreen ? '120px' : '200px',
              },
              isAdding
                ? {
                    ...secondaryButtonStyle,
                    backgroundColor: theme.palette.bidlogiq.taupe,
                    color: theme.palette.getContrastText(theme.palette.bidlogiq.taupe),
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '.MuiButton-startIcon .MuiCircularProgress-root': {
                      color: theme.palette.bidlogiq?.primaryOrange,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.greyLight,
                      color: theme.palette.bidlogiq.taupe,
                    },
                  }
                : secondaryButtonStyle,
            ]}
          >
            {isPursued
              ? 'Already Pursued'
              : isClosed
                ? 'Closed'
                : isAdding
                  ? 'Adding...'
                  : isSmallScreen
                    ? 'Add'
                    : 'Add to Go/No-Go'}
          </Button>
          <Tooltip
            title="Exact billing depends on the length and complexity of the tender documents and provided context."
            slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
          >
            <EstimatedCostChip
              estimate={pricingSettings?.estimates?.capability_analysis || 'Est. $20.00 – $60.00'}
            />
          </Tooltip>
        </Box>
        {isMdUp ? (
          <Button
            variant="contained"
            size="medium"
            startIcon={<ArchiveIcon />}
            onClick={onArchive}
            disabled={isLoading}
            sx={primaryButtonStyle}
          >
            Archive
          </Button>
        ) : (
          <IconButton
            onClick={onArchive}
            disabled={isLoading}
            title="Hide Tender from Store"
            sx={{
              color: theme.palette.secondary.dark,
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.dark, 0.1),
              },
            }}
          >
            <ArchiveIcon fontSize="large" />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );
};

export default PublicTenderCard;
